/*
 * Pure calendar geometry for the Appointments & Reminders tab: mapping
 * reminders to events, month/week grids, time-grid layout, agenda grouping
 * and label formatting. No React, no API calls.
 */
import dayjs from "dayjs";

import type {
  ReminderColor,
  ReminderKind,
  ReminderStatus
} from "@/api/types/foreignNationalReminder";
import {
  isAllDay,
  reminderColor,
  reminderKind,
  utcDayKey,
  type CalendarReminder
} from "./remindersModel";

// Views -----------------------------------------------------------------------

export type CalendarView = "month" | "week" | "day" | "agenda";

export const CALENDAR_VIEWS: ReadonlyArray<{
  id: CalendarView;
  label: string;
  shortcut: string;
}> = [
  { id: "month", label: "Month", shortcut: "M" },
  { id: "week", label: "Week", shortcut: "W" },
  { id: "day", label: "Day", shortcut: "D" },
  { id: "agenda", label: "Agenda", shortcut: "A" }
];

export const isCalendarView = (value: unknown): value is CalendarView =>
  CALENDAR_VIEWS.some((v) => v.id === value);

export const AGENDA_DAYS = 30;

// Events ----------------------------------------------------------------------

/** Where an event comes from: our reminders or a teammate's synced calendar. */
export type CalendarEventSource = "reminder" | "google" | "outlook";

/** Reminder palette plus a muted tone for external (read-only) events. */
export type CalendarEventColor = ReminderColor | "neutral";

export type CalendarEvent = {
  id: string;
  title: string;
  description: string;
  /** Local instants. All-day events start at local midnight of their day. */
  start: Date;
  /** Inclusive of the last covered instant; for all-day events, end-of-day. */
  end: Date;
  allDay: boolean;
  kind: ReminderKind;
  status: ReminderStatus;
  color: CalendarEventColor;
  location: string;
  assignees: string[];
  systemManaged: boolean;
  source: CalendarEventSource;
  /** The client the event belongs to (reminders only; none for a firm entry). */
  clientId?: string;
  clientName?: string;
  /** Link back to the provider's event (synced events only). */
  externalUrl?: string;
  /** Whose connected calendar the event came from (synced events only). */
  ownerUid?: string;
  /** Every teammate's calendar the same event is on, once duplicates merge. */
  ownerUids?: string[];
  /** The backing record; absent for synced events. */
  reminder?: CalendarReminder;
};

const MINUTE_MS = 60 * 1000;
/** Timed reminders have no duration; give them a readable block. */
export const POINT_EVENT_MINUTES = 30;

const parseUtcDay = (key: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

export const toCalendarEvent = (
  r: CalendarReminder,
  clientName?: string
): CalendarEvent | null => {
  const allDay = isAllDay(r);
  let start: Date | null;
  let end: Date | null;
  if (allDay) {
    start = parseUtcDay(utcDayKey(r.due_date));
    const endDay = r.end_date ? parseUtcDay(utcDayKey(r.end_date)) : null;
    end = dayjs(endDay ?? start ?? undefined)
      .endOf("day")
      .toDate();
  } else {
    const ms = Date.parse(r.due_date ?? "");
    start = Number.isFinite(ms) ? new Date(ms) : null;
    const endMs = Date.parse(r.end_date ?? "");
    end = Number.isFinite(endMs)
      ? new Date(Math.max(endMs, ms))
      : start
      ? new Date(start.getTime() + POINT_EVENT_MINUTES * MINUTE_MS)
      : null;
  }
  if (!start || !end) return null;
  return {
    id: r.reminder_id,
    title: (r.title ?? "").trim() || "(Untitled)",
    description: (r.description ?? "").trim(),
    start,
    end,
    allDay,
    kind: reminderKind(r),
    status: r.status,
    color: reminderColor(r),
    location: (r.location ?? "").trim(),
    assignees: r.assigned_to_user_ids ?? [],
    systemManaged: Boolean(r.system_managed),
    source: "reminder",
    clientId: r.foreign_national_id ?? undefined,
    clientName: clientName?.trim() || undefined,
    reminder: r
  };
};

const sortEvents = (events: CalendarEvent[]): CalendarEvent[] =>
  events.sort((a, b) => {
    if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
    return a.start.getTime() - b.start.getTime();
  });

/**
 * Reminders → events. `clientNames` (by foreign national id) labels events
 * when they are shown outside a single client's profile.
 */
export const toCalendarEvents = (
  reminders: CalendarReminder[],
  clientNames?: Record<string, string | undefined>
): CalendarEvent[] =>
  sortEvents(
    reminders
      .map((r) =>
        toCalendarEvent(
          r,
          r.foreign_national_id
            ? clientNames?.[r.foreign_national_id]
            : undefined
        )
      )
      .filter((e): e is CalendarEvent => e !== null)
  );

/** A synced event from a teammate's Google or Outlook calendar. */
export type ExternalEventRow = {
  id: string;
  uid: string;
  /** ISO instants; all-day rows carry `YYYY-MM-DD` dates. */
  start: string;
  end: string | null;
  all_day: boolean;
  title: string;
  source: "google" | "outlook";
  html_link?: string | null;
};

const parseExternalInstant = (
  value: string | null | undefined,
  allDay: boolean
): Date | null => {
  if (!value) return null;
  if (allDay) {
    const day = parseUtcDay(value.slice(0, 10));
    return day;
  }
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? new Date(ms) : null;
};

export const toExternalEvent = (
  row: ExternalEventRow
): CalendarEvent | null => {
  const allDay = Boolean(row.all_day);
  const start = parseExternalInstant(row.start, allDay);
  if (!start) return null;
  let end: Date;
  if (allDay) {
    // Providers send an exclusive end date for all-day events.
    const endDay = parseExternalInstant(row.end, true);
    const lastDay =
      endDay && endDay.getTime() > start.getTime()
        ? dayjs(endDay).subtract(1, "day")
        : dayjs(start);
    end = lastDay.endOf("day").toDate();
  } else {
    const endInstant = parseExternalInstant(row.end, false);
    end =
      endInstant && endInstant.getTime() > start.getTime()
        ? endInstant
        : new Date(start.getTime() + POINT_EVENT_MINUTES * MINUTE_MS);
  }
  return {
    id: `${row.source}:${row.uid}:${row.id}`,
    title: (row.title ?? "").trim() || "(Busy)",
    description: "",
    start,
    end,
    allDay,
    kind: "APPOINTMENT",
    status: "OPEN",
    color: "neutral",
    location: "",
    assignees: [row.uid],
    systemManaged: false,
    source: row.source,
    ownerUid: row.uid,
    externalUrl: row.html_link ?? undefined
  };
};

/** Synced rows → read-only events, sorted like reminders. */
export const toExternalEvents = (rows: ExternalEventRow[]): CalendarEvent[] =>
  sortEvents(
    rows.map(toExternalEvent).filter((e): e is CalendarEvent => e !== null)
  );

const duplicateKey = (e: CalendarEvent): string =>
  [
    e.allDay ? "d" : "t",
    e.start.getTime(),
    e.end.getTime(),
    e.title.trim().toLowerCase()
  ].join("|");

/**
 * One event per meeting: the same synced event on two teammates' calendars
 * (same title, start and end) becomes one, carrying every owner in
 * `ownerUids`. Reminders are never merged.
 */
export const mergeDuplicateEvents = (
  events: CalendarEvent[]
): CalendarEvent[] => {
  const byKey = new Map<string, CalendarEvent>();
  const out: CalendarEvent[] = [];
  events.forEach((e) => {
    if (e.source === "reminder") {
      out.push(e);
      return;
    }
    const key = duplicateKey(e);
    const first = byKey.get(key);
    if (!first) {
      const copy = { ...e, ownerUids: e.ownerUid ? [e.ownerUid] : [] };
      byKey.set(key, copy);
      out.push(copy);
      return;
    }
    if (e.ownerUid && !first.ownerUids?.includes(e.ownerUid)) {
      first.ownerUids = [...(first.ownerUids ?? []), e.ownerUid];
    }
    first.assignees = Array.from(new Set([...first.assignees, ...e.assignees]));
  });
  return out;
};

/** Merge reminder and synced events into one sorted list. */
export const mergeEvents = (...lists: CalendarEvent[][]): CalendarEvent[] =>
  sortEvents(lists.flat());

// Days ------------------------------------------------------------------------

export const dayKey = (d: Date | dayjs.Dayjs): string =>
  dayjs(d).format("YYYY-MM-DD");

export const isSameDay = (a: Date, b: Date): boolean =>
  dayjs(a).isSame(b, "day");

export type DayCell = {
  date: Date;
  key: string;
  isOutside: boolean;
  isToday: boolean;
};

/** Six Sunday-first weeks (42 cells) covering the anchor's month. */
export const monthGrid = (
  anchor: Date,
  today: Date = new Date()
): DayCell[] => {
  const first = dayjs(anchor).startOf("month");
  const gridStart = first.startOf("week");
  return Array.from({ length: 42 }, (_, i) => {
    const d = gridStart.add(i, "day");
    return {
      date: d.toDate(),
      key: d.format("YYYY-MM-DD"),
      isOutside: !d.isSame(first, "month"),
      isToday: d.isSame(today, "day")
    };
  });
};

export const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** The seven days (Sunday first) of the anchor's week. */
export const weekDays = (anchor: Date): Date[] => {
  const start = dayjs(anchor).startOf("week");
  return Array.from({ length: 7 }, (_, i) => start.add(i, "day").toDate());
};

export const rangeTitle = (view: CalendarView, anchor: Date): string => {
  const a = dayjs(anchor);
  switch (view) {
    case "month":
      return a.format("MMMM YYYY");
    case "week": {
      const start = a.startOf("week");
      const end = a.endOf("week");
      if (start.isSame(end, "month")) {
        return `${start.format("MMM D")} - ${end.format("D, YYYY")}`;
      }
      if (start.isSame(end, "year")) {
        return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
      }
      return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
    }
    case "day":
      return a.format("dddd, MMMM D, YYYY");
    case "agenda": {
      const end = a.add(AGENDA_DAYS - 1, "day");
      return a.isSame(end, "year")
        ? `${a.format("MMM D")} - ${end.format("MMM D, YYYY")}`
        : `${a.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
    }
    default:
      return a.format("MMMM YYYY");
  }
};

/** Move the anchor one page forward (+1) or back (-1) for the view. */
export const shift = (view: CalendarView, anchor: Date, dir: 1 | -1): Date => {
  const a = dayjs(anchor);
  switch (view) {
    case "month":
      return a.startOf("month").add(dir, "month").toDate();
    case "week":
      return a.add(dir * 7, "day").toDate();
    case "day":
      return a.add(dir, "day").toDate();
    case "agenda":
      return a.add(dir * AGENDA_DAYS, "day").toDate();
    default:
      return anchor;
  }
};

// Selection helpers -----------------------------------------------------------

const overlapsDay = (e: CalendarEvent, day: Date): boolean => {
  const start = dayjs(day).startOf("day").valueOf();
  const end = dayjs(day).endOf("day").valueOf();
  return e.start.getTime() <= end && e.end.getTime() >= start;
};

/** Every event touching the day (all-day first, then by start). */
export const eventsOnDay = (
  events: CalendarEvent[],
  day: Date
): CalendarEvent[] => events.filter((e) => overlapsDay(e, day));

/** True for all-day events and timed events that cross midnight. */
export const rendersInAllDayRow = (e: CalendarEvent): boolean =>
  e.allDay || !isSameDay(e.start, e.end);

/** Timed, single-day events for the time grid. */
export const timedEventsOnDay = (
  events: CalendarEvent[],
  day: Date
): CalendarEvent[] =>
  events.filter((e) => !rendersInAllDayRow(e) && isSameDay(e.start, day));

export type AllDaySpan = {
  event: CalendarEvent;
  /** 0-based column of the first visible day. */
  startCol: number;
  /** Number of visible columns covered (>= 1). */
  span: number;
  /** True when the event started before the visible range. */
  clippedStart: boolean;
  /** True when the event ends after the visible range. */
  clippedEnd: boolean;
  /** Stacking row so overlapping bars do not collide. */
  lane: number;
};

/** Bars for the all-day row across `days` (a week or a single day). */
export const allDaySpans = (
  events: CalendarEvent[],
  days: Date[]
): AllDaySpan[] => {
  if (days.length === 0) return [];
  const rangeStart = dayjs(days[0]).startOf("day");
  const rangeEnd = dayjs(days[days.length - 1]).endOf("day");
  const spans: AllDaySpan[] = [];
  events
    .filter(rendersInAllDayRow)
    .filter(
      (e) =>
        e.start.getTime() <= rangeEnd.valueOf() &&
        e.end.getTime() >= rangeStart.valueOf()
    )
    .sort((a, b) => a.start.getTime() - b.start.getTime())
    .forEach((event) => {
      const startDay = dayjs(event.start).startOf("day");
      const endDay = dayjs(event.end).startOf("day");
      const clippedStart = startDay.isBefore(rangeStart);
      const clippedEnd = endDay.isAfter(rangeEnd);
      const startCol = clippedStart ? 0 : startDay.diff(rangeStart, "day");
      const endCol = clippedEnd
        ? days.length - 1
        : Math.min(endDay.diff(rangeStart, "day"), days.length - 1);
      const span = Math.max(1, endCol - startCol + 1);
      // First lane with no overlap.
      let lane = 0;
      const collides = (s: AllDaySpan) =>
        s.lane === lane &&
        s.startCol <= startCol + span - 1 &&
        startCol <= s.startCol + s.span - 1;
      while (spans.some(collides)) lane += 1;
      spans.push({ event, startCol, span, clippedStart, clippedEnd, lane });
    });
  return spans;
};

export type TimedLayout = {
  event: CalendarEvent;
  /** Percent offsets/height relative to the 24h column. */
  topPct: number;
  heightPct: number;
  /** Overlap column index and count for side-by-side rendering. */
  column: number;
  columns: number;
};

const minuteOfDay = (d: Date, day: Date): number =>
  Math.max(
    0,
    Math.min(24 * 60, dayjs(d).diff(dayjs(day).startOf("day"), "minute"))
  );

/**
 * Position timed events within a day column. Overlapping events are split
 * into columns (classic "cluster" algorithm) so they render side by side.
 */
export const layoutTimedEvents = (
  dayEvents: CalendarEvent[],
  day: Date
): TimedLayout[] => {
  const items = dayEvents
    .map((event) => {
      const startMin = minuteOfDay(event.start, day);
      const endMin = Math.max(
        startMin + POINT_EVENT_MINUTES,
        minuteOfDay(event.end, day)
      );
      return { event, startMin, endMin: Math.min(endMin, 24 * 60) };
    })
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);

  const out: TimedLayout[] = [];
  let cluster: typeof items = [];
  let clusterEnd = -1;

  const flush = () => {
    if (cluster.length === 0) return;
    const columnsEnd: number[] = [];
    const placed = cluster.map((it) => {
      let col = columnsEnd.findIndex((end) => end <= it.startMin);
      if (col === -1) {
        col = columnsEnd.length;
        columnsEnd.push(it.endMin);
      } else {
        columnsEnd[col] = it.endMin;
      }
      return { ...it, col };
    });
    const columns = columnsEnd.length;
    placed.forEach((it) => {
      out.push({
        event: it.event,
        topPct: (it.startMin / (24 * 60)) * 100,
        heightPct: ((it.endMin - it.startMin) / (24 * 60)) * 100,
        column: it.col,
        columns
      });
    });
    cluster = [];
    clusterEnd = -1;
  };

  items.forEach((it) => {
    if (cluster.length > 0 && it.startMin >= clusterEnd) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.endMin);
  });
  flush();
  return out;
};

/**
 * Per day column: how many timed events end above the scrolled-to minute,
 * and where the latest of them starts (to scroll back to it).
 */
export const earlierCounts = (
  layouts: TimedLayout[][],
  topMinute: number
): Array<{ count: number; firstTopPct: number }> =>
  layouts.map((day) => {
    const hidden = day.filter(
      (l) => ((l.topPct + l.heightPct) / 100) * 24 * 60 <= topMinute + 1
    );
    return {
      count: hidden.length,
      firstTopPct: hidden.reduce((max, l) => Math.max(max, l.topPct), 0)
    };
  });

export type AgendaDay = { date: Date; key: string; events: CalendarEvent[] };

/** Days (with events) from the anchor for `days` days, in order. */
export const agendaDays = (
  events: CalendarEvent[],
  anchor: Date,
  days: number = AGENDA_DAYS
): AgendaDay[] => {
  const start = dayjs(anchor).startOf("day");
  const out: AgendaDay[] = [];
  for (let i = 0; i < days; i += 1) {
    const d = start.add(i, "day");
    const list = eventsOnDay(events, d.toDate());
    if (list.length > 0) {
      out.push({ date: d.toDate(), key: d.format("YYYY-MM-DD"), events: list });
    }
  }
  return out;
};

// Formatting ------------------------------------------------------------------

/** "10am", "2:30pm", "12pm". */
export const formatTimeShort = (d: Date): string => {
  const t = dayjs(d);
  return t.minute() === 0 ? t.format("ha") : t.format("h:mma");
};

/** "All day", "10am", "10am - 11am"; multi-day gets dates. */
export const formatEventTime = (e: CalendarEvent): string => {
  if (e.allDay) {
    if (isSameDay(e.start, e.end)) return "All day";
    return `${dayjs(e.start).format("MMM D")} - ${dayjs(e.end).format(
      "MMM D"
    )}`;
  }
  const isPoint =
    e.kind === "REMINDER" &&
    e.end.getTime() - e.start.getTime() <= POINT_EVENT_MINUTES * MINUTE_MS;
  if (isPoint) return formatTimeShort(e.start);
  if (isSameDay(e.start, e.end)) {
    return `${formatTimeShort(e.start)} - ${formatTimeShort(e.end)}`;
  }
  return `${dayjs(e.start).format("MMM D, h:mma")} - ${dayjs(e.end).format(
    "MMM D, h:mma"
  )}`;
};

export const EVENT_COLOR_CLASSES: Record<
  CalendarEventColor,
  { pill: string; dot: string; swatch: string }
> = {
  neutral: {
    pill: "bg-fill-2 text-fg hover:bg-fill-3",
    dot: "bg-fg-4",
    swatch: "bg-fg-4"
  },
  sky: {
    pill: "bg-info-soft text-info hover:bg-info-soft",
    dot: "bg-info",
    swatch: "bg-info"
  },
  amber: {
    pill: "bg-warn-soft text-warn hover:bg-warn-soft",
    dot: "bg-warn",
    swatch: "bg-warn"
  },
  violet: {
    pill: "bg-purple-soft text-purple hover:bg-purple-soft",
    dot: "bg-purple",
    swatch: "bg-purple"
  },
  rose: {
    pill: "bg-danger-soft text-danger hover:bg-danger-soft",
    dot: "bg-danger",
    swatch: "bg-danger"
  },
  emerald: {
    pill: "bg-success-soft text-success hover:bg-success-soft",
    dot: "bg-success",
    swatch: "bg-success"
  },
  orange: {
    pill: "bg-orange-soft text-orange hover:bg-orange-soft",
    dot: "bg-orange",
    swatch: "bg-orange"
  }
};

export type EventTone = "default" | "done" | "overdue" | "external";

/**
 * Closed events are muted; open reminders in the past are flagged; synced
 * events are never overdue or done.
 */
export const eventTone = (
  e: CalendarEvent,
  now: Date = new Date()
): EventTone => {
  if (e.source !== "reminder") return "external";
  if (e.status !== "OPEN") return "done";
  if (e.kind === "REMINDER" && e.end.getTime() < now.getTime())
    return "overdue";
  return "default";
};
